/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {AriaRangeCalendarProps, useRangeCalendar} from 'react-aria/useRangeCalendar';

import {CalendarBase} from './CalendarBase';
import {CalendarIdentifier, createCalendar, Calendar as ICalendar} from '@internationalized/date';
import {createDOMRef} from '../utils/useDOMRef';
import {DateValue} from 'react-stately/useCalendarState';
import {FocusableRef, StyleProps} from '@react-types/shared';
import React, {ReactElement, useImperativeHandle, useMemo, useRef} from 'react';
import {useLocale} from 'react-aria/I18nProvider';
import {useProviderProps} from '../provider/Provider';
import {useRangeCalendarState} from 'react-stately/useRangeCalendarState';

export interface SpectrumRangeCalendarProps<T extends DateValue>
  extends Omit<AriaRangeCalendarProps<T>, 'weeksInMonth'>, StyleProps {
  /**
   * The number of months to display at once. Up to 3 months are supported.
   *
   * @default 1
   */
  visibleMonths?: number;

  /**
   * A function to create a new
   * [Calendar](https://react-spectrum.adobe.com/internationalized/date/Calendar.html) object for a
   * given calendar identifier. If not provided, the `createCalendar` function from
   * `@internationalized/date` will be used.
   */
  createCalendar?: (identifier: CalendarIdentifier) => ICalendar;
}

/**
 * RangeCalendars display a grid of days in one or more months and allow users to select a
 * contiguous range of dates.
 */
export const RangeCalendar = React.forwardRef(function RangeCalendar<T extends DateValue>(
  props: SpectrumRangeCalendarProps<T>,
  ref: FocusableRef<HTMLElement>
) {
  props = useProviderProps(props);
  let {visibleMonths = 1} = props;
  visibleMonths = Math.max(visibleMonths, 1);
  let visibleDuration = useMemo(() => ({months: visibleMonths}), [visibleMonths]);
  let {locale} = useLocale();
  let state = useRangeCalendarState({
    ...props,
    locale,
    visibleDuration,
    createCalendar: props.createCalendar || createCalendar
  });

  let domRef = useRef(null);
  useImperativeHandle(ref, () => ({
    ...createDOMRef(domRef),
    focus() {
      state.setFocused(true);
    }
  }));

  let {calendarProps, prevButtonProps, nextButtonProps, errorMessageProps} = useRangeCalendar(
    props,
    state,
    domRef
  );

  return (
    <CalendarBase
      {...props}
      visibleMonths={visibleMonths}
      state={state}
      calendarRef={domRef}
      calendarProps={calendarProps}
      prevButtonProps={prevButtonProps}
      nextButtonProps={nextButtonProps}
      errorMessageProps={errorMessageProps}
    />
  );
}) as <T extends DateValue>(
  props: SpectrumRangeCalendarProps<T> & {ref?: FocusableRef<HTMLElement>}
) => ReactElement;
